<template>
  <div class="overall">
    <div class="chart_title">
      REITs 指数基金
      <span class="chart_title_sub">· 跟踪中证 REITs 全收益指数（932047）</span>
    </div>

    <div class="reits-toolbar">
      <span class="reits-btn-group">
        <el-button
          v-for="opt in shareOptions"
          :key="opt.value"
          size="mini"
          plain
          :class="shareClass === opt.value ? 'isActiveStyle' : ''"
          @click="shareClass = opt.value">
          {{ opt.label }}
        </el-button>
      </span>
      <span class="reits-deadline">
        净值截至 {{ latestDate || '-' }} · {{ sourceLabel }}
      </span>
    </div>

    <el-empty
      v-if="loadError"
      :description="loadError"
      class="reits-empty"></el-empty>

    <template v-else>
      <el-row :gutter="14" v-if="shownFunds.length">
        <el-col
          v-for="fund in shownFunds"
          :key="fund.fund_code"
          :xs="24"
          :sm="12"
          :md="12"
          :lg="6">
          <div
            class="reits-card CHARTS_BGC"
            :class="{ selected: selected === fund.fund_code }"
            @click="selectFund(fund.fund_code)">
            <div class="reits-card-head">
              <span class="reits-card-name">{{ fund.manager }}</span>
              <em class="reits-card-class">{{ fund.share_class }}类</em>
            </div>
            <div class="reits-card-code">{{ fund.fund_code }} · {{ fund.fund_name }}</div>
            <div class="reits-card-nav">
              <span class="reits-card-nav-num">
                {{ latest[fund.fund_code] ? formatNav(latest[fund.fund_code].unit_nav) : '--' }}
              </span>
              <span class="reits-card-nav-label">单位净值</span>
            </div>
            <div class="reits-card-meta">
              <div class="reits-card-meta-row">
                <span class="reits-card-meta-label">募集规模</span>
                <span class="reits-card-meta-value">{{ formatAmount(fund.raised_amount) }}</span>
              </div>
              <div class="reits-card-meta-row">
                <span class="reits-card-meta-label">成立日</span>
                <span class="reits-card-meta-value">{{ formatDate(fund.inception_date) }}</span>
              </div>
              <div class="reits-card-meta-row">
                <span class="reits-card-meta-label">认购户数</span>
                <span class="reits-card-meta-value">{{ formatCount(fund.subscriber_count) }}</span>
              </div>
            </div>
          </div>
        </el-col>
      </el-row>
      <el-empty v-else description="暂无该类基金数据" class="reits-empty"></el-empty>

      <div class="overall2" v-if="series.length">
        <div class="chart_title chart_title_small">
          净值走势 · {{ activeFund ? activeFund.fund_name : '' }}
          <span class="chart_title_sub">· {{ activeFund ? activeFund.share_class : '' }}类</span>
        </div>
        <div ref="chart" class="reits-chart"></div>

        <div class="reits-table">
          <div class="reits-table-head reits-table-row">
            <span>日期</span>
            <span>单位净值</span>
            <span>累计净值</span>
            <span>日收益</span>
          </div>
          <div
            v-for="row in series"
            :key="row.nav_date"
            class="reits-table-row">
            <span>{{ row.nav_date }}</span>
            <span class="reits-table-nav">{{ formatNav(row.unit_nav) }}</span>
            <span class="reits-table-nav">{{ formatNav(row.accum_nav) }}</span>
            <span :class="retClass(row.daily_return)">
              {{ formatReturn(row.daily_return) }}
            </span>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
export default {
  data() {
    return {
      loading: false,
      funds: [],
      series: [],
      selected: '',
      shareClass: 'A',
      shareOptions: [
        { value: 'A', label: 'A 类' },
        { value: 'C', label: 'C 类' },
      ],
      loadError: '',
      chart: null,
    };
  },
  computed: {
    shownFunds() {
      return this.funds.filter((x) => x.share_class === this.shareClass);
    },
    latest() {
      const out = {};
      this.funds.forEach((f) => { if (f.latest) out[f.fund_code] = f.latest; });
      return out;
    },
    activeFund() {
      return this.funds.find((f) => f.fund_code === this.selected);
    },
    latestDate() {
      return this.series.length ? this.series[this.series.length - 1].nav_date : '';
    },
    sourceLabel() {
      if (!this.series.length) return '净值数据';
      const raw = this.series[this.series.length - 1].source || '';
      // 数据源对外口径统一为「净值数据」，不再暴露第三方通道名
      if (!raw || raw === 'eastmoney' || raw === 'tushare') return '净值数据';
      return '净值数据';
    },
  },
  watch: {
    shareClass() {
      const stillVisible = this.shownFunds.some((f) => f.fund_code === this.selected);
      if (!stillVisible && this.shownFunds.length) this.selectFund(this.shownFunds[0].fund_code);
    },
    // 主题切换时 echarts 实例需重画取色
    '$store.state.theme'() {
      this.$nextTick(() => {
        if (this.chart) {
          this.chart.resize();
          this.renderChart();
        }
      });
    },
    series() {
      // series 变化后 v-if 切换容器；ResizeObserver 会在容器显示时触发 init/resize
      this.$nextTick(() => this.renderChart());
    },
  },
  mounted() {
    this.reload();
    window.addEventListener('resize', this.onResize);
    // 用 ResizeObserver 监听 chart 容器真实尺寸，等有了宽度再 init
    this.$nextTick(() => {
      if (this.$refs.chart && window.ResizeObserver) {
        this._resizeObs = new ResizeObserver((entries) => {
          const w = entries[0].contentRect.width;
          const h = entries[0].contentRect.height;
          if (w > 100 && this.chart) {
            this.chart.resize();
          } else if (w > 100 && !this.chart) {
            this.renderChart();
          }
        });
        this._resizeObs.observe(this.$refs.chart);
      }
    });
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);
    if (this._resizeObs) { this._resizeObs.disconnect(); this._resizeObs = null; }
    if (this.chart) this.chart.dispose();
    this.chart = null;
  },
  methods: {
    async reload() {
      this.loading = true;
      this.loadError = '';
      try {
        const list = (await this.$https.get('/api/v4/reits_etf/list')).data.data || [];
        this.funds = list;
        const firstVisible = this.shownFunds[0];
        if (firstVisible) {
          await this.selectFund(firstVisible.fund_code);
        } else {
          this.loadError = '当前测试环境尚未发布 REITs 指数基金接口';
        }
      } catch (error) {
        const code = error && error.response && error.response.status;
        this.loadError = code === 404
          ? '当前测试环境尚未发布 REITs 指数基金接口'
          : '净值接口暂不可用，请稍后重试';
      } finally {
        this.loading = false;
      }
    },
    async selectFund(code) {
      this.selected = code;
      try {
        const data = (await this.$https.get('/api/v4/reits_etf/series', { params: { fund_code: code } })).data.data || [];
        this.series = data;
        const last = data[data.length - 1];
        if (last) {
          this.funds = this.funds.map((f) => (f.fund_code === code ? { ...f, latest: last } : f));
        }
        // 双 RAF 兜底：v-if 切换后 DOM 才能拿到尺寸
        this.$nextTick(() => {
          requestAnimationFrame(() => {
            this.renderChart();
            setTimeout(() => this.chart && this.chart.resize(), 60);
          });
        });
      } catch (error) {
        const status = error && error.response && error.response.status;
        this.loadError = status === 404
          ? '当前测试环境尚未发布 REITs 指数基金接口'
          : '净值接口暂不可用，请稍后重试';
      }
    },
    renderChart() {
      if (!this.$refs.chart) return;
      const el = this.$refs.chart;
      // 容器还没显示（v-if 刚切）就轮询等真实宽度，避免 init 出 0 宽竖条
      if (el.clientWidth < 50) {
        requestAnimationFrame(() => {
          if (this.$refs.chart && this.$refs.chart.clientWidth >= 50) {
            this.renderChart();
          } else if (this.$refs.chart) {
            setTimeout(() => this.renderChart(), 100);
          }
        });
        return;
      }
      if (!this.chart) this.chart = this.$echarts.init(el);
      this.chart.clear();
      this.chart.resize();
      if (!this.series.length) return;
      const theme = this.$store.state.theme;
      const dates = this.series.map((r) => r.nav_date);
      const values = this.series.map((r) => Number(r.unit_nav) || 0);
      const returns = this.series.map((r) => Number(r.daily_return) || 0);
      const accent = this.$store.state.colors
        ? this.$store.state.colors.find((c) => c.name === '公募REITs')
        : null;
      const lineColor = (accent && accent.value) || (theme === 'dark' ? '#5ae8ed' : '#00aec7');
      const axisColor = theme === 'dark' ? 'rgba(218, 218, 218, 1)' : '#403e3e';
      const splitColor = theme === 'dark' ? 'rgba(255,255,255,0.05)' : '#f0f2f5';
      const labelColor = theme === 'dark' ? '#dadada' : '#403e3e';
      const tooltipBg = theme === 'dark' ? '#3b3d42' : '#fff';
      this.chart.setOption({
        backgroundColor: 'transparent',
        animation: true,
        animationDuration: 800,
        animationEasing: 'cubicOut',
        animationDelay: (idx) => idx * 80,
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'cross' },
          backgroundColor: tooltipBg,
          borderColor: theme === 'dark' ? '#44464a' : '#ebebeb',
          textStyle: { color: labelColor },
          formatter: (params) => {
            const idx = params[0].dataIndex;
            return `${dates[idx]}<br/>单位净值：${values[idx].toFixed(4)}<br/>日收益：${returns[idx]}%`;
          },
        },
        grid: { left: 60, right: 30, top: 30, bottom: 50 },
        xAxis: {
          type: 'category',
          data: dates,
          axisLine: { lineStyle: { color: axisColor } },
          axisLabel: { color: axisColor, fontSize: 12 },
        },
        yAxis: {
          type: 'value',
          scale: true,
          axisLine: { lineStyle: { color: axisColor } },
          axisLabel: { color: axisColor, fontSize: 12, formatter: (v) => v.toFixed(4) },
          splitLine: { lineStyle: { color: splitColor } },
        },
        series: [{
          name: '单位净值',
          type: 'line',
          smooth: true,
          data: values,
          symbol: 'circle',
          symbolSize: 6,
          lineStyle: { color: lineColor, width: 2 },
          itemStyle: { color: lineColor, borderColor: lineColor },
          areaStyle: {
            color: {
              type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: theme === 'dark' ? 'rgba(90,232,237,0.35)' : 'rgba(0,174,199,0.2)' },
                { offset: 1, color: 'rgba(90,232,237,0)' },
              ],
            },
          },
        }],
      });
    },
    onResize() { if (this.chart) this.chart.resize(); },
    formatAmount(value) {
      if (value === '' || value === null || value === undefined) return '--';
      const n = Number(value);
      if (!Number.isFinite(n)) return '--';
      return (n / 100000000).toFixed(2) + ' 亿元';
    },
    formatCount(value) {
      if (value === '' || value === null || value === undefined) return '--';
      const n = Number(value);
      return Number.isFinite(n) ? n.toLocaleString('zh-CN') + ' 户' : '--';
    },
    formatDate(value) {
      if (!value || value === 'None') return '--';
      return value;
    },
    formatNav(value) {
      if (value === '' || value === null || value === undefined) return '--';
      const n = Number(value);
      return Number.isFinite(n) ? n.toFixed(4) : '--';
    },
    formatReturn(value) {
      if (value === '' || value === null || value === undefined) return '--';
      const n = Number(value);
      if (!Number.isFinite(n)) return '--';
      return (n > 0 ? '+' : '') + n.toFixed(2) + '%';
    },
    retClass(value) {
      if (value === '' || value === null || value === undefined) return '';
      const n = Number(value);
      if (!Number.isFinite(n) || n === 0) return '';
      return n > 0 ? 'ret-up' : 'ret-down';
    },
  },
};
</script>

<style lang="less" scoped>
// —— 完全按项目 ZouShi 风格：overall + chart_title + CHARTS_BGC ——

.overall {
  background-color: var(--charts_bgc);
  height: auto;
  padding-top: calc(var(--ROOTFONTFIZE) * (15 / 144));
  border-radius: calc(var(--ROOTFONTFIZE) * (20 / 144));
  margin-bottom: calc(var(--ROOTFONTFIZE) * (39 / 144));
  padding-right: calc(var(--ROOTFONTFIZE) * (16 / 144));
  padding-bottom: calc(var(--ROOTFONTFIZE) * (20 / 144));
  box-sizing: border-box;
}

.overall2 {
  background-color: var(--charts_bgc);
  height: auto;
  margin-top: calc(var(--ROOTFONTFIZE) * (5 / 144));
  padding-top: calc(var(--ROOTFONTFIZE) * (15 / 144));
  border-radius: calc(var(--ROOTFONTFIZE) * (20 / 144));
  margin-bottom: calc(var(--ROOTFONTFIZE) * (20 / 144));
  padding-right: calc(var(--ROOTFONTFIZE) * (16 / 144));
  padding-bottom: calc(var(--ROOTFONTFIZE) * (20 / 144));
  box-sizing: border-box;
}

.chart_title {
  color: var(--chart_title);
  font-size: calc(var(--ROOTFONTFIZE) * (20 / 144));
  font-weight: 700;
  border-left: calc(var(--ROOTFONTFIZE) * (5 / 144)) solid var(--primary-color);
  padding-left: calc(var(--ROOTFONTFIZE) * (18 / 144));
  height: calc(var(--ROOTFONTFIZE) * (28 / 144));
  display: flex;
  align-items: center;
}
.chart_title_small {
  font-size: calc(var(--ROOTFONTFIZE) * (16 / 144));
  font-weight: 500;
  margin-bottom: calc(var(--ROOTFONTFIZE) * (12 / 144));
}
.chart_title_sub {
  margin-left: calc(var(--ROOTFONTFIZE) * (8 / 144));
  font-size: calc(var(--ROOTFONTFIZE) * (12 / 144));
  font-weight: 400;
  color: var(--deadline_style_color);
}

.reits-toolbar {
  margin-top: calc(var(--ROOTFONTFIZE) * (27 / 144));
  margin-bottom: calc(var(--ROOTFONTFIZE) * (20 / 144));
  margin-left: calc(var(--ROOTFONTFIZE) * (18 / 144));
  display: flex;
  justify-content: flex-start;
  align-items: center;
}
.reits-deadline {
  margin-left: auto;
}
.reits-btn-group {
  display: flex;
  align-items: center;
}
.reits-btn-group .el-button {
  width: 56px;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px solid var(--Btn-borderColor) !important;
  box-sizing: border-box !important;
  height: 32px;
  background-color: var(--Btn-bgc2) !important;
  color: var(--stock_area_item_color1) !important;
  font-size: calc(var(--ROOTFONTFIZE) * (12 / 144));
  margin-right: 6px;
  transition: border-color 0.15s, color 0.15s;
}
.reits-btn-group .el-button:hover,
.reits-btn-group .el-button:focus {
  background-color: var(--Btn-bgc2) !important;
  color: var(--stock_area_item_color1) !important;
  border-color: var(--primary-color) !important;
}
.reits-btn-group .isActiveStyle,
.reits-btn-group .isActiveStyle:hover,
.reits-btn-group .isActiveStyle:focus {
  background-color: var(--primary-color) !important;
  color: var(--Btn-color) !important;
  border-color: var(--primary-color) !important;
}
.reits-deadline {
  font-size: calc(var(--ROOTFONTFIZE) * (12 / 144));
  color: var(--deadline_style_color);
}

// —— A/C 类切换：复用项目 el-radio-group 样式（已有主题变量适配），不覆盖

// —— 卡片：CHARTS_BGC 已给背景+边框，这里只补 padding/选中态/文字色 ——
.reits-card {
  padding: calc(var(--ROOTFONTFIZE) * (16 / 144));
  margin-bottom: calc(var(--ROOTFONTFIZE) * (14 / 144));
  cursor: pointer;
  border-radius: calc(var(--ROOTFONTFIZE) * (14 / 144));
  transition: border-color 0.15s, box-shadow 0.15s, transform 0.15s;
  box-sizing: border-box;
}
.reits-card:hover {
  border-color: var(--primary-color) !important;
  transform: translateY(-1px);
}
.reits-card.selected {
  border-color: var(--primary-color) !important;
  box-shadow: 0 0 calc(var(--ROOTFONTFIZE) * (8 / 144)) rgba(90, 232, 237, 0.25);
}
.reits-card-head {
  display: flex;
  align-items: center;
  margin-bottom: calc(var(--ROOTFONTFIZE) * (6 / 144));
}
.reits-card-name {
  font-size: calc(var(--ROOTFONTFIZE) * (15 / 144));
  font-weight: 700;
  color: var(--chart_title);
}
.reits-card-class {
  margin-left: calc(var(--ROOTFONTFIZE) * (8 / 144));
  padding: 1px calc(var(--ROOTFONTFIZE) * (6 / 144));
  font-style: normal;
  font-size: calc(var(--ROOTFONTFIZE) * (11 / 144));
  font-weight: 500;
  color: var(--primary-color);
  background-color: rgba(90, 232, 237, 0.12);
  border-radius: calc(var(--ROOTFONTFIZE) * (4 / 144));
}
.reits-card-code {
  color: var(--deadline_style_color);
  font-size: calc(var(--ROOTFONTFIZE) * (12 / 144));
  margin-bottom: calc(var(--ROOTFONTFIZE) * (10 / 144));
}
.reits-card-nav {
  display: flex;
  align-items: baseline;
  margin-bottom: calc(var(--ROOTFONTFIZE) * (10 / 144));
}
.reits-card-nav-num {
  font-size: calc(var(--ROOTFONTFIZE) * (26 / 144));
  font-weight: 700;
  color: var(--primary-color);
  font-family: 'conghuaEN', sans-serif;
}
.reits-card-nav-label {
  margin-left: calc(var(--ROOTFONTFIZE) * (6 / 144));
  font-size: calc(var(--ROOTFONTFIZE) * (12 / 144));
  color: var(--deadline_style_color);
}
.reits-card-meta {
  border-top: 1px solid rgba(135, 135, 135, 0.25);
  padding-top: calc(var(--ROOTFONTFIZE) * (8 / 144));
}
.reits-card-meta-row {
  display: flex;
  justify-content: space-between;
  font-size: calc(var(--ROOTFONTFIZE) * (12 / 144));
  line-height: calc(var(--ROOTFONTFIZE) * (20 / 144));
}
.reits-card-meta-label {
  color: var(--deadline_style_color);
}
.reits-card-meta-value {
  color: var(--chart_title);
  font-weight: 500;
}

// —— 净值走势图 ——
.reits-chart {
  width: 100%;
  height: calc(var(--ROOTFONTFIZE) * (240 / 144));
  min-height: 240px;
  margin-top: calc(var(--ROOTFONTFIZE) * (8 / 144));
}

// —— 净值明细表 ——
.reits-table {
  margin-top: calc(var(--ROOTFONTFIZE) * (16 / 144));
  border-top: 1px solid rgba(135, 135, 135, 0.2);
}
.reits-table-row {
  display: grid;
  grid-template-columns: 1.2fr 1fr 1fr 1fr;
  padding: calc(var(--ROOTFONTFIZE) * (8 / 144)) 0;
  border-bottom: 1px solid rgba(135, 135, 135, 0.15);
  font-size: calc(var(--ROOTFONTFIZE) * (13 / 144));
  color: var(--deadline_style_color);
}
.reits-table-head {
  font-weight: 600;
  color: var(--chart_title);
  background-color: rgba(135, 135, 135, 0.05);
  border-bottom: 1px solid rgba(135, 135, 135, 0.3);
}
.reits-table-nav {
  color: var(--chart_title);
  font-family: 'conghuaEN', sans-serif;
}
.ret-up { color: #f56c6c; font-weight: 600; }
.ret-down { color: #67c23a; font-weight: 600; }

// —— 空状态 ——
.reits-empty {
  margin: calc(var(--ROOTFONTFIZE) * (30 / 144)) 0;
}
</style>